<template>
    <div class="video-player">
        <!-- Video container with dual elements -->
        <div class="video-container bg-dark rounded position-relative">
            <video
                ref="videoA"
                class="video-element"
                :class="{ 'video-active': activeSlot === 'A' }"
                crossorigin="anonymous"
                playsinline
                @timeupdate="onTimeUpdateA"
                @ended="onSegmentEndedA"
                @loadedmetadata="onMetadataLoaded('A', $event)"
                @canplay="onCanPlay('A')"
                @waiting="onWaitingA"
                @playing="onPlayingA"
            />
            <video
                ref="videoB"
                class="video-element"
                :class="{ 'video-active': activeSlot === 'B' }"
                crossorigin="anonymous"
                playsinline
                @timeupdate="onTimeUpdateB"
                @ended="onSegmentEndedB"
                @loadedmetadata="onMetadataLoaded('B', $event)"
                @canplay="onCanPlay('B')"
                @waiting="onWaitingB"
                @playing="onPlayingB"
            />

            <!-- Loading spinner overlay -->
            <div
                v-if="loading || isBuffering || isSeekingSegment"
                class="video-overlay d-flex align-items-center justify-content-center"
            >
                <div class="spinner-border text-light" />
            </div>

            <!-- Empty state when no segments (only once loading has finished) -->
            <div
                v-if="!loading && !segments.length"
                class="video-overlay d-flex flex-column align-items-center justify-content-center"
            >
                <font-awesome-icon
                    icon="video"
                    class="text-white mb-2"
                    style="font-size: 2em;"
                />
                <span class="text-white small">{{ $t('video_recordings.no_video_segments') }}</span>
            </div>
        </div>

        <!-- Timeline -->
        <video-timeline
            class="mt-3"
            :global-time="globalTime"
            :total-duration="totalDuration"
            :segments="playlist"
            :buffered-indices="bufferedIndices"
            :is-playing="isPlaying"
            @seek="onSeek"
        />

        <!-- Controls -->
        <video-controls
            class="mt-2"
            :is-playing="isPlaying"
            :playback-rate="playbackRate"
            :has-previous="currentIndex > 0"
            :has-next="currentIndex < playlist.length - 1"
            @play="play"
            @pause="pause"
            @skip-next="skipNext"
            @skip-prev="skipPrev"
            @seek-by="seekBy"
            @speed-change="setPlaybackRate"
            @create-observation="onCreateObservation"
        />
    </div>
</template>

<script>
/**
 * VideoPlayer
 * ===========
 * Plays a trip's dashcam footage, which arrives as multiple short MP4
 * SEGMENTS (~2 min each) rather than one file. Two responsibilities make
 * this non-trivial:
 *
 * 1. GAPLESS PLAYBACK ACROSS SEGMENTS (dual-video A/B technique)
 *    A single <video> element stalls visibly when its `src` changes between
 *    segments. To avoid that we keep TWO <video> elements (slots A and B):
 *    one plays the current segment while the other silently preloads the
 *    next. When the current segment ends we swap which slot is visible
 *    (`activeSlot`), so playback continues without a gap. Because both
 *    elements fire the same DOM events, every handler is split into an
 *    A/B pair (e.g. onTimeUpdateA/onTimeUpdateB) that ignores events from
 *    the non-active slot — otherwise the preloading video's events would
 *    corrupt the timeline.
 *
 * 2. REPORTING TIME FOR MAP SYNC
 *    `time-update` is emitted with two values:
 *      - globalTime  : position within the concatenated playlist (seconds)
 *      - elapsedSec  : seconds since recording began, derived from each
 *                      segment's own `recordedAt`. This is what the map uses
 *                      to place the route marker. It's computed per-segment
 *                      (not as a single running total) so gaps between
 *                      segment recordings are handled correctly, and it's
 *                      expressed as a delta from the first segment's start so
 *                      it's independent of the segments' absolute clock
 *                      (their `recordedAt` carries a timezone error — see
 *                      VideoRecordingsDetail for the full explanation).
 *    `seekToElapsed()` is the inverse, used when the user clicks the map.
 */
import VideoTimeline from './VideoTimeline.vue'
import VideoControls from './VideoControls.vue'


export default {
    name: 'VideoPlayer',
    components: {
        VideoTimeline,
        VideoControls
    },
    props: {
        segments: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    emits: ['time-update', 'segment-change', 'create-observation'],
    data() {
        return {
            playlist: [],
            currentIndex: 0,
            activeSlot: 'A',
            loadedIndexA: -1,
            loadedIndexB: -1,
            isPlaying: false,
            isBuffering: false,
            isSeekingSegment: false,
            pendingSeekSlot: null,
            pendingSeekCleanup: null,
            globalTime: 0,
            playbackRate: 1,
            bufferedIndices: [],
            estimatedSegmentDuration: 120,
            preloadWindow: 2,
            // Seek step sizes (seconds). Small = ±1s buttons and Left/Right
            // arrows; large = ±5s buttons and Shift+Left/Right.
            seekStepSmall: 1,
            seekStepLarge: 5
        }
    },
    computed: {
        totalDuration() {
            if (!this.playlist.length) return 0
            return this.playlist.reduce((sum, seg) => sum + seg.duration, 0)
        },
        // Recording start of the first segment, in ms. Used to express the
        // current frame as "seconds since recording began" — which avoids
        // comparing the video clock and route clock in absolute terms
        // (they can differ, e.g. timezone-mislabelled recordedAt values).
        videoStartMs() {
            if (!this.playlist.length) return null
            const first = this.playlist[0]
            return first.recordedAt ? new Date(first.recordedAt).getTime() : null
        },
        activeVideo() {
            return this.activeSlot === 'A' ? this.$refs.videoA : this.$refs.videoB
        },
        standbyVideo() {
            return this.activeSlot === 'A' ? this.$refs.videoB : this.$refs.videoA
        }
    },
    watch: {
        segments: {
            handler(newSegments) {
                this.buildPlaylist(newSegments)
            },
            immediate: true
        },
        // Keep both <video> elements in sync with the selected rate. Assigning
        // a new `src` + calling load() resets an element's playbackRate to the
        // browser default (1), so any code path that reloads/swaps an element
        // must re-apply the rate. Watching here catches ref-level drift; the
        // per-play re-assertion in applyPlaybackRate() covers swaps and reloads.
        playbackRate(rate) {
            if (this.$refs.videoA) this.$refs.videoA.playbackRate = rate
            if (this.$refs.videoB) this.$refs.videoB.playbackRate = rate
        }
    },
    beforeUnmount() {
        this.cleanup()
    },
    methods: {
        buildPlaylist(segments) {
            if (!segments || !segments.length) {
                this.cleanup()
                this.playlist = []
                this.currentIndex = 0
                this.globalTime = 0
                this.isPlaying = false
                this.bufferedIndices = []
                return
            }

            // Sort by segmentIndex and build cumulative timeline
            const sorted = [...segments].sort((a, b) => a.segmentIndex - b.segmentIndex)
            let cumulative = 0
            this.playlist = sorted.map((seg) => {
                const item = {
                    id: seg.id,
                    index: seg.segmentIndex,
                    url: seg.downloadUrl,
                    recordedAt: seg.recordedAt,
                    duration: seg.duration || this.estimatedSegmentDuration,
                    durationKnown: !!seg.duration,
                    startTime: cumulative
                }
                cumulative += item.duration
                return item
            })

            // Reset state and load first segment
            this.currentIndex = 0
            this.globalTime = 0
            this.isPlaying = false
            this.bufferedIndices = []
            this.$nextTick(() => {
                this.loadSegment(this.currentIndex, 'A')
                this.preloadNext()
            })
        },

        loadSegment(index, slot, seekTo) {
            if (index < 0 || index >= this.playlist.length) return

            const video = slot === 'A' ? this.$refs.videoA : this.$refs.videoB

            if (!video) return

            // Record which segment is loaded into this slot only once we know a
            // real load is happening. Setting it before the !video guard could
            // leave a stale index pointing at nothing, which onMetadataLoaded
            // would then misattribute.
            if (slot === 'A') {
                this.loadedIndexA = index
            } else {
                this.loadedIndexB = index
            }

            const segment = this.playlist[index]
            video.src = segment.url
            video.playbackRate = this.playbackRate
            video.load()

            if (seekTo !== undefined) {
                video.addEventListener('loadedmetadata', () => {
                    video.currentTime = seekTo
                }, { once: true })
            }
        },

        preloadNext() {
            const nextIndex = this.currentIndex + 1
            if (nextIndex >= this.playlist.length) return
            if (this.bufferedIndices.includes(nextIndex)) return

            const standbySlot = this.activeSlot === 'A' ? 'B' : 'A'
            this.loadSegment(nextIndex, standbySlot)
        },

        onMetadataLoaded(slot, event) {
            const video = event.target
            const duration = video.duration

            if (!isFinite(duration) || duration <= 0) return

            const index = slot === 'A' ? this.loadedIndexA : this.loadedIndexB

            if (index >= 0 && index < this.playlist.length && !this.playlist[index].durationKnown) {
                const oldDuration = this.playlist[index].duration

                this.playlist[index].duration = duration
                this.playlist[index].durationKnown = true

                // Recalculate cumulative start times from this index onward
                if (oldDuration !== duration) {
                    this.recalculateStartTimes(index + 1)
                }
            }
        },

        recalculateStartTimes(fromIndex) {
            for (let i = fromIndex; i < this.playlist.length; i++) {
                const prev = this.playlist[i - 1]
                this.playlist[i].startTime = prev.startTime + prev.duration
            }
        },

        onCanPlay(slot) {
            // Ignore the standby slot while a seek is loading into it — its
            // index isn't currentIndex+1, and finishStandbySeek handles its
            // buffered flag on swap.
            if (slot === this.pendingSeekSlot) return

            let index
            if (slot === this.activeSlot) {
                index = this.currentIndex
            } else {
                index = this.currentIndex + 1
            }

            if (index >= 0 && index < this.playlist.length && !this.bufferedIndices.includes(index)) {
                this.bufferedIndices.push(index)
            }
        },

        onTimeUpdateA() {
            if (this.activeSlot === 'A') this.onTimeUpdate()
        },

        onTimeUpdateB() {
            if (this.activeSlot === 'B') this.onTimeUpdate()
        },

        onTimeUpdate() {
            if (!this.activeVideo) return

            const localTime = this.activeVideo.currentTime
            const segment = this.playlist[this.currentIndex]
            if (!segment) return

            this.globalTime = segment.startTime + localTime

            // Seconds elapsed since recording began. Computed from each
            // segment's own recordedAt so gaps between segments are handled,
            // but expressed as a delta so it's independent of the absolute
            // clock (video and route timestamps may not share the same zone).
            let elapsedSinceStart = null
            if (segment.recordedAt && this.videoStartMs !== null) {
                const frameMs = new Date(segment.recordedAt).getTime() + (localTime * 1000)
                elapsedSinceStart = (frameMs - this.videoStartMs) / 1000
            }
            this.$emit('time-update', this.globalTime, elapsedSinceStart)

            // Preload next segment when 80% through current
            if (segment.durationKnown && localTime > segment.duration * 0.8) {
                this.preloadNext()
            }
        },

        onSegmentEndedA() {
            if (this.activeSlot === 'A') this.onSegmentEnded()
        },

        onSegmentEndedB() {
            if (this.activeSlot === 'B') this.onSegmentEnded()
        },

        onSegmentEnded() {
            const nextIndex = this.currentIndex + 1
            if (nextIndex >= this.playlist.length) {
                // End of all segments
                this.isPlaying = false
                return
            }

            // Swap to next segment
            this.currentIndex = nextIndex
            this.activeSlot = this.activeSlot === 'A' ? 'B' : 'A'
            this.$emit('segment-change', this.currentIndex)

            if (this.isPlaying) {
                this.applyPlaybackRate()
                this.activeVideo.play()
            }

            // Preload the one after next
            this.$nextTick(() => {
                this.preloadNext()
            })
        },

        onWaitingA() {
            if (this.activeSlot === 'A') this.isBuffering = true
        },

        onWaitingB() {
            if (this.activeSlot === 'B') this.isBuffering = true
        },

        onPlayingA() {
            if (this.activeSlot === 'A') {
                this.isBuffering = false
                // Re-assert the selected rate whenever the active element
                // actually starts playing. Covers every resume path (initial
                // play, segment swap, run switch) in one place — assigning
                // src + load() resets an element's playbackRate to 1, and
                // without this the video would drop to 1x while the UI, bound
                // to this.playbackRate, keeps showing the selected speed.
                this.applyPlaybackRate()
            }
        },

        onPlayingB() {
            if (this.activeSlot === 'B') {
                this.isBuffering = false
                this.applyPlaybackRate()
            }
        },

        // Seek by seconds elapsed since recording began. Finds the segment
        // covering that elapsed time and seeks within it. Handles gaps by
        // snapping to the nearest segment boundary.
        seekToElapsed(elapsedSec) {
            if (!this.playlist.length || this.videoStartMs === null) {
                return false
            }

            const targetMs = this.videoStartMs + (elapsedSec * 1000)

            for (let i = 0; i < this.playlist.length; i++) {
                const seg = this.playlist[i]
                if (!seg.recordedAt) continue
                const segStartMs = new Date(seg.recordedAt).getTime()
                const segEndMs = segStartMs + (seg.duration * 1000)

                if (targetMs >= segStartMs && targetMs < segEndMs) {
                    const localTime = (targetMs - segStartMs) / 1000
                    this.onSeek(seg.startTime + localTime)
                    return true
                }
            }

            // Not inside any segment (gap or out of range): snap to nearest end.
            if (targetMs < this.videoStartMs) {
                this.onSeek(0)
                return true
            }
            const last = this.playlist[this.playlist.length - 1]
            this.onSeek(last.startTime)
            return true
        },

        onSeek(targetGlobalTime) {
            if (!this.playlist.length) return
            // Find target segment via binary search
            const targetIndex = this.findSegmentAtTime(targetGlobalTime)
            if (targetIndex < 0) return

            const segment = this.playlist[targetIndex]
            const localTime = targetGlobalTime - segment.startTime

            if (targetIndex === this.currentIndex) {
                // Same segment — just seek within the active video.
                this.cancelPendingSeek()
                this.activeVideo.currentTime = localTime
                this.globalTime = targetGlobalTime
                this.$emit('time-update', this.globalTime, this.elapsedForGlobalTime(targetGlobalTime))
            } else {
                // Different segment. To avoid a visible flash of the target
                // segment's first frame, we DON'T reload the active (visible)
                // element. Instead we load the target into the STANDBY slot,
                // seek it to localTime while it stays hidden, and only flip
                // `activeSlot` once it has decoded the target frame. The old
                // frame stays on screen until the clean cut.
                this.seekIntoStandby(targetIndex, localTime, targetGlobalTime)
            }
        },

        // Load `index` at `localTime` into the standby slot, then swap it in
        // once it has actually seeked to that frame. Keeps the current frame
        // visible in the meantime (no first-frame flash, no spinner).
        seekIntoStandby(index, localTime, targetGlobalTime) {
            // Abandon any seek already in flight.
            this.cancelPendingSeek()

            const standbySlot = this.activeSlot === 'A' ? 'B' : 'A'
            const standby = standbySlot === 'A' ? this.$refs.videoA : this.$refs.videoB
            if (!standby) return

            // Pause the currently-visible video so it stops emitting
            // timeupdates for the OLD position while we prepare the swap
            // (otherwise it would fight the marker we move below). It stays
            // visible — we just freeze it. Playback resumes on the new slot
            // after the swap if we were playing.
            if (this.activeVideo) {
                this.activeVideo.pause()
            }

            const segment = this.playlist[index]

            // Report the new position immediately so the map marker / timeline
            // move right away, even though the video frame swaps a beat later.
            this.globalTime = targetGlobalTime
            this.$emit('time-update', this.globalTime, this.elapsedForGlobalTime(targetGlobalTime))

            // (Re)load the standby element with the target segment.
            standby.src = segment.url
            standby.playbackRate = this.playbackRate
            standby.load()

            // Dim + spinner over the frozen frame so the wait reads as
            // "loading new position" rather than "stuck".
            this.isSeekingSegment = true

            const applySeek = () => {
                // Seek within the freshly loaded standby element.
                standby.currentTime = localTime
            }

            const onStandbySeeked = () => {
                // The standby element now shows the target frame — swap it in
                // as the active slot in one clean cut.
                this.finishStandbySeek(standbySlot, index)
            }

            // Once metadata is known we can set currentTime; once the seek has
            // applied, swap. Track listeners so a rapid second click can undo.
            standby.addEventListener('loadedmetadata', applySeek, { once: true })
            standby.addEventListener('seeked', onStandbySeeked, { once: true })

            this.pendingSeekSlot = standbySlot
            this.pendingSeekCleanup = () => {
                standby.removeEventListener('loadedmetadata', applySeek)
                standby.removeEventListener('seeked', onStandbySeeked)
            }
        },

        finishStandbySeek(slot, index) {
            this.pendingSeekCleanup = null
            this.pendingSeekSlot = null
            this.isSeekingSegment = false

            // Promote the standby slot to active; its segment becomes current.
            this.activeSlot = slot
            this.currentIndex = index

            // Only the current (and its follower) buffered flags remain valid.
            this.bufferedIndices = this.bufferedIndices.filter(i => i === index || i === index + 1)
            if (!this.bufferedIndices.includes(index)) {
                this.bufferedIndices.push(index)
            }

            if (this.isPlaying) {
                this.applyPlaybackRate()
                this.activeVideo.play()
            }

            this.$emit('segment-change', this.currentIndex)

            // Preload the following segment into the (new) standby slot.
            this.$nextTick(() => {
                this.preloadNext()
            })
        },

        cancelPendingSeek() {
            if (this.pendingSeekCleanup) {
                this.pendingSeekCleanup()
            }
            this.pendingSeekCleanup = null
            this.pendingSeekSlot = null
            this.isSeekingSegment = false
        },

        // Seconds since recording began, for a given position in the
        // concatenated timeline. Mirrors the delta emitted by onTimeUpdate.
        elapsedForGlobalTime(globalTime) {
            const index = this.findSegmentAtTime(globalTime)
            if (index < 0) return null
            const seg = this.playlist[index]
            if (!seg.recordedAt || this.videoStartMs === null) return null
            const localTime = globalTime - seg.startTime
            const frameMs = new Date(seg.recordedAt).getTime() + (localTime * 1000)
            return (frameMs - this.videoStartMs) / 1000
        },

        findSegmentAtTime(globalTime) {
            // Binary search for the segment containing globalTime
            let lo = 0
            let hi = this.playlist.length - 1

            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2)
                const seg = this.playlist[mid]
                const segEnd = seg.startTime + seg.duration

                if (globalTime < seg.startTime) {
                    hi = mid - 1
                } else if (globalTime >= segEnd) {
                    lo = mid + 1
                } else {
                    return mid
                }
            }

            // Clamp to last segment if beyond end
            return Math.min(lo, this.playlist.length - 1)
        },

        play() {
            if (!this.activeVideo || !this.playlist.length) return
            this.activeVideo.play()
            this.isPlaying = true
        },

        pause() {
            if (!this.activeVideo) return
            this.activeVideo.pause()
            this.isPlaying = false
        },

        // Pause playback, then ask the parent to open the observation flow.
        // Pausing first freezes the frame the user is about to capture.
        onCreateObservation() {
            this.pause()
            this.$emit('create-observation')
        },

        // Grab the currently displayed frame from the ACTIVE slot's <video>
        // element and return it as a JPEG File (the format the observation
        // photo upload expects). Returns null if no frame is available or if
        // the canvas is tainted (cross-origin video without CORS headers).
        // Requires crossorigin="anonymous" on the <video> elements AND the
        // segment storage to send Access-Control-Allow-Origin.
        captureFrame() {
            const video = this.activeVideo
            if (!video || !video.videoWidth || !video.videoHeight) {
                return Promise.resolve(null)
            }

            const canvas = document.createElement('canvas')
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            canvas.getContext('2d').drawImage(video, 0, 0)

            return new Promise((resolve) => {
                try {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                resolve(null)
                                return
                            }
                            resolve(new File([blob], `video_frame_${Date.now()}.jpg`, { type: 'image/jpeg' }))
                        },
                        'image/jpeg',
                        0.9
                    )
                } catch (e) {
                    // SecurityError: canvas tainted by cross-origin video.
                    resolve(null)
                }
            })
        },

        // Seek the playback position by a signed offset in seconds, clamped to
        // the playlist bounds. Reuses onSeek() so cross-segment seeks stay
        // gapless and the time-update event fires for map sync. No-op when
        // there is no footage. Called by the seek buttons (via the seek-by
        // event) and by the parent's arrow-key handler (via $refs).
        seekBy(offsetSeconds) {
            if (!this.playlist.length || !this.totalDuration) return
            const target = Math.min(
                Math.max(this.globalTime + offsetSeconds, 0),
                this.totalDuration
            )
            this.onSeek(target)
        },

        skipNext() {
            const nextIndex = this.currentIndex + 1
            if (nextIndex >= this.playlist.length) return
            this.onSeek(this.playlist[nextIndex].startTime)
        },

        skipPrev() {
            // If more than 3 seconds into current segment, restart it
            const localTime = this.activeVideo ? this.activeVideo.currentTime : 0
            if (localTime > 3 && this.currentIndex >= 0) {
                this.onSeek(this.playlist[this.currentIndex].startTime)
            } else if (this.currentIndex > 0) {
                this.onSeek(this.playlist[this.currentIndex - 1].startTime)
            }
        },

        setPlaybackRate(rate) {
            this.playbackRate = rate
            if (this.$refs.videoA) this.$refs.videoA.playbackRate = rate
            if (this.$refs.videoB) this.$refs.videoB.playbackRate = rate
        },

        // Re-assert the selected rate on the currently-active element. Must be
        // called before/after resuming playback on an element that may have
        // been (re)loaded — assigning src + load() resets playbackRate to 1,
        // so segment swaps (onSegmentEnded, finishStandbySeek) and playlist
        // rebuilds (run switch) would otherwise drop back to 1x while the UI,
        // bound to this.playbackRate, keeps showing the selected speed.
        applyPlaybackRate() {
            if (this.activeVideo) {
                this.activeVideo.playbackRate = this.playbackRate
            }
        },

        cleanup() {
            this.cancelPendingSeek()
            if (this.$refs.videoA) {
                this.$refs.videoA.pause()
                this.$refs.videoA.src = ''
            }
            if (this.$refs.videoB) {
                this.$refs.videoB.pause()
                this.$refs.videoB.src = ''
            }
        }
    }
}
</script>

<style scoped>
.video-container {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
}

.video-element {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.05s ease;
}

.video-element.video-active {
    opacity: 1;
}

.video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 2;
}
</style>
